import React from 'react';
import { Dropdown, Button } from 'antd';
import { MoreOutlined, CheckOutlined } from '@ant-design/icons';
import { MenuProps } from 'antd/lib';
import useTranslation from '@hooks/useTranslation';
import styles from './GlobalVariablesPanel.module.css';

type Props = {
  /** Handler for download action */
  onDownload: () => void;
  /** Handler for copy action */
  onCopy: () => void;
  /** Handler for paste action */
  onPaste: () => void;
  /** Handler for import action */
  onImport: () => void;
  /** Whether paste option should be enabled */
  canPaste: boolean;
  /** Handler for menu open/close state changes */
  onOpenChange?: (open: boolean) => void;
  /** Whether to save variable values in recipe (only shown when onSaveValuesToggle is provided) */
  saveValuesEnabled?: boolean;
  /** Handler for toggling save values setting (when provided, shows the toggle option) */
  onSaveValuesToggle?: () => void;
};

/**
 * Menu component for import/export operations on global variables
 */
const ImportExportMenu = (props: Props) => {
  const { onDownload, onCopy, onPaste, onImport, canPaste, onOpenChange, saveValuesEnabled, onSaveValuesToggle } = props;
  const t = useTranslation('GlobalVariablesPanel');

  const menuItems: MenuProps['items'] = [
    {
      key: 'download',
      label: t('DownloadConfiguration'),
      onClick: onDownload,
    },
    {
      key: 'copy',
      label: t('CopyConfiguration'),
      onClick: onCopy,
    },
    {
      key: 'paste',
      label: t('PasteConfiguration'),
      onClick: onPaste,
      disabled: !canPaste,
    },
    {
      key: 'import',
      label: t('ImportConfiguration'),
      onClick: onImport,
    },
    // Only show the save values toggle when handler is provided
    ...(onSaveValuesToggle ? [
      {
        type: 'divider' as const,
      },
      {
        key: 'saveValues',
        label: t('SaveValuesInRecipe'),
        icon: saveValuesEnabled ? <CheckOutlined /> : null,
        onClick: onSaveValuesToggle,
      },
    ] : []),
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomRight"
      onOpenChange={onOpenChange}
    >
      <Button
        type="text"
        icon={<MoreOutlined />}
        className={styles.AddButton}
        title={t('ImportExportMenu')}
      />
    </Dropdown>
  );
};

export default ImportExportMenu;

